import React, { useEffect, useState } from "react";
import { ArrowRight, Phone, Truck, Sparkle, Broom, Recycle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { useSeo } from "@/lib/useSeo";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { MobileActionBar } from "@/components/MobileActionBar";
import { ZipChecker, quoteHrefForZip } from "@/components/ZipChecker";

const NAVY = "#182848";
const ORANGE = "#EE7623";
const SLATE = "#4A5A72";
const CREAM = "#FDF3EA";

const CITIES = ["Gwynn Oak", "Baltimore", "Windsor Mill", "Catonsville", "Pikesville", "Randallstown", "Ellicott City", "Halethorpe", "Stevenson"];

export const SERVICE_AREA_SEO = {
  title: "Service Area | Cleaning & Junk Removal Across Maryland | HCOB",
  description: "Serving Gwynn Oak, Pikesville, Catonsville, Randallstown, Ellicott City, Windsor Mill and all of Baltimore — plus statewide Maryland. Check your ZIP in seconds.",
  path: "/service-area",
  og: {
    "og:title": "Where We Work | HCOB Property Services",
    "og:site_name": "HCOB Property Services",
    "og:description": "Cleaning, junk removal and property services across Baltimore, Baltimore County, Howard County and all of Maryland. Check your ZIP.",
    "og:url": "https://hcobnetwork.com/service-area",
    "og:type": "website",
  },
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://hcobnetwork.com/#localbusiness",
    name: "HCOB Property Services",
    url: "https://hcobnetwork.com/service-area",
    telephone: "(410) 870-9347",
    image: "https://hcobnetwork.com/og-default.png",
    address: { "@type": "PostalAddress", addressLocality: "Gwynn Oak", addressRegion: "MD", postalCode: "21207", addressCountry: "US" },
    areaServed: [
      ...CITIES.map((c) => ({ "@type": "City", name: c })),
      { "@type": "State", name: "Maryland" },
    ],
    openingHoursSpecification: [{ "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], opens: "00:00", closes: "23:59" }],
  },
};

// Default groups are rendered at build time (pre-render) and swapped for the live admin config on load.
export const DEFAULT_GROUPS = [
  { name: "Gwynn Oak & Woodlawn", communities: "Gwynn Oak, Windsor Mill, Woodlawn", zips: ["21207", "21244"] },
  { name: "Pikesville & Northwest", communities: "Pikesville, Randallstown, Stevenson, Mount Washington, Park Heights", zips: ["21208", "21209", "21215", "21133", "21153"] },
  { name: "Catonsville & Southwest", communities: "Catonsville, Halethorpe, Irvington, Arbutus", zips: ["21228", "21227", "21229"] },
  { name: "Ellicott City & Howard County", communities: "Ellicott City", zips: ["21043"] },
  { name: "West Baltimore", communities: "Walbrook, Sandtown, Franklin Square, Edmondson", zips: ["21216", "21217", "21223"] },
  { name: "North & Central Baltimore", communities: "Roland Park, Hampden, Govans, Charles Village, Remington", zips: ["21210", "21211", "21212", "21218"] },
  { name: "Downtown & South Baltimore", communities: "Downtown, Inner Harbor, Federal Hill, Locust Point", zips: ["21201", "21202", "21230"] },
];

const TIERS = [
  { tier: "Core Service Area", coverage: "The 21 ZIP codes above — roughly a 7-mile ring around Gwynn Oak", get: "Fastest scheduling, often same week. Crews are already working these streets daily." },
  { tier: "Greater Baltimore", coverage: "Baltimore City & County, Howard, Anne Arundel, Carroll, Harford beyond the core ring", get: "Full service, scheduled. Standard pricing." },
  { tier: "Statewide Maryland", coverage: "Everywhere else in Maryland", get: "Full service, scheduled in advance. Larger jobs and commercial accounts welcome." },
];

const SERVICES = [
  { label: "Junk Removal", href: "/junk-removal", Icon: Truck, blurb: "Single items to full cleanouts — quoted from your photos." },
  { label: "Basement & Garage Cleanouts", href: "/basement-garage-cleanout", Icon: Truck, blurb: "Stairs included. Garage bays from $375, packed basements from $525." },
  { label: "Deep Cleaning", href: "/deep-cleaning", Icon: Sparkle, blurb: "Top-to-bottom reset for kitchens, baths and every room." },
  { label: "Carpet Cleaning", href: "/carpet-cleaning", Icon: Sparkle, blurb: "Up to 3 rooms $199. Pet and smoke odor, stairs, quoted from photos." },
  { label: "Move-Out Cleaning", href: "/move-out-cleaning", Icon: Broom, blurb: "Deposit-ready: inside cabinets, inside appliances, every surface." },
  { label: "Rental Turnovers", href: "/landlord-turnover", Icon: Truck, blurb: "Eviction cleanouts, cleaning, paint and repairs — one vendor, one invoice." },
  { label: "Routine Cleaning", href: "/cleaning", Icon: Broom, blurb: "Weekly, biweekly or monthly — your checklist, your schedule." },
  { label: "Fresh Start Cleanouts", href: "/fresh-start", Icon: Recycle, blurb: "Deep clean + junk removal + carpet care in one visit." },
  { label: "Hoarding Cleanup", href: "/hoarding-cleanup", Icon: Recycle, blurb: "Judgment-free clear-out, deep clean and odor treatment — quoted from photos." },
];

const NAV_ANCHORS = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#services", label: "All 13+ Services" },
  { href: "/#quote", label: "Get a Quote", primary: true },
];

const Eyebrow = ({ children }) => (
  <div className="font-mono-label text-[10px] tracking-[0.26em]" style={{ color: ORANGE }}>{children}</div>
);
const H2 = ({ children, testid }) => (
  <h2 className="font-display mt-3 text-base font-black leading-[1.1] tracking-tight md:text-lg" style={{ color: NAVY, fontSize: "clamp(26px, 3.4vw, 40px)" }} data-testid={testid}>{children}</h2>
);
const Section = ({ id, bg, children, testid }) => (
  <section id={id} data-testid={testid} className="border-b border-[#182848]/10" style={{ backgroundColor: bg }}>
    <div className="mx-auto max-w-[1200px] px-5 py-14 md:px-10 md:py-24">{children}</div>
  </section>
);

export default function ServiceArea() {
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [checkedZip, setCheckedZip] = useState(null);
  useSeo(SERVICE_AREA_SEO);

  useEffect(() => {
    track("service_area_view", { page_path: "/service-area" });
    api.get("/service-area/config").then(({ data }) => { if (data?.groups?.length) setGroups(data.groups); }).catch(() => {});
  }, []);

  const quoteHref = checkedZip ? quoteHrefForZip(checkedZip) : "/#quote";

  return (
    <div className="min-h-screen antialiased" style={{ backgroundColor: "#FFFFFF", color: NAVY }} data-testid="service-area-page">
      <SiteHeader anchors={NAV_ANCHORS} brandTestId="service-area-brand" />

      {/* ── Hero + ZIP checker ── */}
      <section id="top" data-testid="service-area-hero" className="border-b border-[#182848]/10" style={{ backgroundColor: CREAM }}>
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-5 py-14 md:px-10 md:py-24 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
          <Eyebrow>Service area · Based in Gwynn Oak, MD 21207</Eyebrow>
          <h1 className="font-display mt-4 text-4xl font-black leading-[0.98] tracking-tighter sm:text-5xl lg:text-6xl" style={{ color: NAVY }} data-testid="service-area-h1">
            Where We Work
          </h1>
          <p className="mt-6 max-w-[640px] text-[15px] leading-[1.65] md:text-base" style={{ color: SLATE }}>
            Our crews are based in Gwynn Oak and work Baltimore City, Baltimore County and Howard County every day — and we take jobs across all of Maryland. Check your ZIP below and we'll tell you straight.
          </p>
          <ZipChecker onResult={(r) => setCheckedZip(r?.serviceable ? r.zip : null)} />
          <p className="mt-6 text-[12px] leading-relaxed" style={{ color: SLATE }} data-testid="service-area-trust-strip">
            Based in Gwynn Oak &nbsp;•&nbsp; Serving all of Maryland &nbsp;•&nbsp; Same crews, same prices, wherever you are
          </p>
          <p className="mt-3 text-[14px] font-bold">
            <a href="/same-day-cleaning" data-testid="need-it-today" className="underline underline-offset-4" style={{ color: "#9A4512" }}>Need it today? Same-day cleaning in Baltimore — request by noon.</a>
          </p>
          </div>
          <aside className="hidden lg:col-span-5 lg:block lg:pl-6 lg:pt-10" aria-label="Coverage at a glance" data-testid="service-area-glance">
            <div className="rounded-sm border bg-white/70 p-6" style={{ borderColor: "rgba(24,40,72,0.15)" }}>
              <div className="font-mono-label text-[9px] tracking-[0.26em]" style={{ color: ORANGE }}>At a glance</div>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-6">
                {[
                  ["21207", "Home base · Gwynn Oak"],
                  ["~7 mi", "Core ring around Gwynn Oak"],
                  [`${groups.reduce((n, g) => n + g.zips.length, 0)}`, "Core ZIP codes"],
                  ["MD", "Statewide coverage"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <dt className="font-mono-label text-[9px] tracking-[0.22em]" style={{ color: SLATE }}>{l}</dt>
                    <dd className="font-display mt-1 text-3xl font-black leading-none tracking-tight" style={{ color: NAVY }}>{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 border-t pt-4 text-[13px] leading-snug" style={{ borderColor: "rgba(24,40,72,0.12)", color: SLATE }}>
                Baltimore City, Baltimore County and Howard County every day. Everywhere else in Maryland by scheduled booking.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* ── Core service area ── */}
      <Section id="core-area" bg="#FFFFFF" testid="service-area-core">
        <Eyebrow>Core service area</Eyebrow>
        <H2 testid="core-area-heading">Our Core Service Area</H2>
        <p className="mt-5 max-w-[680px] text-[15px] leading-[1.65] md:text-base" style={{ color: SLATE }}>
          We're based in Gwynn Oak, and these are the neighborhoods our crews are in most days of the week. If you're here, we can usually get to you fast — our trucks are already nearby.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="core-area-groups">
          {groups.map((g) => (
            <article key={g.name} className="rounded-sm border p-5" style={{ borderColor: "rgba(24,40,72,0.15)", backgroundColor: CREAM }} data-testid="core-area-group">
              <div className="font-mono-label text-[9px] tracking-[0.24em]" style={{ color: ORANGE }}>Area group</div>
              <h3 className="font-display mt-1 text-[17px] font-black leading-tight" style={{ color: NAVY }}>{g.name}</h3>
              <div className="font-mono-label mt-4 text-[9px] tracking-[0.24em]" style={{ color: SLATE }}>Communities</div>
              <p className="mt-1 text-[14px] leading-snug" style={{ color: NAVY }}>{g.communities}</p>
              <div className="font-mono-label mt-4 text-[9px] tracking-[0.24em]" style={{ color: SLATE }}>ZIP codes</div>
              <ul className="mt-1.5 flex flex-wrap gap-1.5" aria-label={`${g.name} ZIP codes`}>
                {g.zips.map((z) => (
                  <li key={z} className="rounded-sm px-2 py-1 font-mono-label text-[11px] font-bold tracking-[0.08em] text-white" style={{ backgroundColor: NAVY }}>{z}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <p className="mt-8 text-[14px]" style={{ color: SLATE }}>
          Don't see your ZIP?{" "}
          <a href="#top" className="font-bold underline underline-offset-2" style={{ color: NAVY }} data-testid="core-area-check-above">Check above</a>{" "}
          — we cover all of Maryland, and most of the state is a scheduled booking away.
        </p>
      </Section>

      {/* ── Tiered coverage ── */}
      <Section id="coverage" bg={CREAM} testid="service-area-tiers">
        <Eyebrow>Coverage tiers</Eyebrow>
        <H2 testid="tiers-heading">We Serve All of Maryland</H2>
        <p className="mt-5 max-w-[680px] text-[15px] leading-[1.65] md:text-base" style={{ color: SLATE }}>
          The core area above is where we're fastest. But the network reaches the whole state — here's how it works.
        </p>
        <div className="mt-10 overflow-hidden rounded-sm border" style={{ borderColor: "rgba(24,40,72,0.15)" }} role="table" aria-label="Coverage tiers">
          <div role="row" className="hidden grid-cols-[1fr_1.5fr_1.7fr] gap-6 px-6 py-3 font-mono-label text-[9px] tracking-[0.24em] text-white md:grid" style={{ backgroundColor: NAVY }}>
            <div role="columnheader">Tier</div><div role="columnheader">Coverage</div><div role="columnheader">What you get</div>
          </div>
          {TIERS.map((t, i) => (
            <div key={t.tier} role="row" className="grid grid-cols-1 gap-3 border-t px-6 py-5 md:grid-cols-[1fr_1.5fr_1.7fr] md:gap-6" style={{ borderColor: "rgba(24,40,72,0.12)", backgroundColor: i % 2 ? "rgba(255,255,255,0.5)" : "#FFFFFF" }} data-testid="tier-row">
              <div role="cell">
                <div className="font-mono-label text-[9px] tracking-[0.24em] md:hidden" style={{ color: ORANGE }}>Tier</div>
                <div className="font-display text-[17px] font-black" style={{ color: NAVY }}>{t.tier}</div>
              </div>
              <div role="cell">
                <div className="font-mono-label text-[9px] tracking-[0.24em] md:hidden" style={{ color: SLATE }}>Coverage</div>
                <div className="text-[14px] leading-snug" style={{ color: NAVY }}>{t.coverage}</div>
              </div>
              <div role="cell">
                <div className="font-mono-label text-[9px] tracking-[0.24em] md:hidden" style={{ color: SLATE }}>What you get</div>
                <div className="text-[14px] leading-snug" style={{ color: SLATE }}>{t.get}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Why local ── */}
      <Section id="why-local" bg="#FFFFFF" testid="service-area-why-local">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Eyebrow>Local crews</Eyebrow>
            <H2 testid="why-local-heading">Why It Matters That We're Actually From Here</H2>
          </div>
          <div className="md:col-span-7 md:pt-8">
            <p className="text-[15px] leading-[1.7] md:text-base" style={{ color: SLATE }}>
              National junk removal and cleaning franchises dispatch from wherever the nearest territory happens to be. Sometimes that's close. Sometimes it's an hour away and you're waiting all afternoon.
            </p>
            <p className="mt-5 text-[15px] leading-[1.7] md:text-base" style={{ color: SLATE }}>
              We're a Baltimore company with Baltimore crews. We know which rowhome blocks have no alley access, which buildings need a service elevator reserved, and which neighborhoods you can't park a box truck on after 4 p.m. That knowledge is the difference between a job that runs smooth and a job that runs long.
            </p>
            <p className="font-mono-label mt-8 text-[11px] tracking-[0.3em]" style={{ color: ORANGE }} data-testid="why-local-tagline">
              By the Community. For the Community.
            </p>
          </div>
        </div>
      </Section>

      {/* ── Services grid ── */}
      <Section id="services" bg={CREAM} testid="service-area-services">
        <Eyebrow>Services</Eyebrow>
        <H2 testid="services-heading">What We Do in Your Area</H2>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map(({ label, href, Icon, blurb }) => (
            <a
              key={href}
              href={href}
              onClick={() => track("service_page_click", { service: label, href })}
              data-testid={`service-card-${href.slice(1)}`}
              className="group flex min-h-[160px] flex-col justify-between rounded-sm border bg-white p-5 transition-transform hover:-translate-y-0.5"
              style={{ borderColor: "rgba(24,40,72,0.15)" }}
            >
              <div>
                <Icon size={26} weight="duotone" style={{ color: ORANGE }} />
                <div className="font-display mt-4 text-[18px] font-black leading-tight" style={{ color: NAVY }}>{label}</div>
                <p className="mt-2 text-[13px] leading-snug" style={{ color: SLATE }}>{blurb}</p>
              </div>
              <div className="mt-5 inline-flex items-center gap-1 text-[13px] font-bold" style={{ color: NAVY }}>
                See pricing &amp; details <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </div>
            </a>
          ))}
        </div>
      </Section>

      {/* ── Final CTA ── */}
      <section id="quote-cta" data-testid="service-area-cta" style={{ backgroundColor: NAVY, color: "#FFFFFF" }}>
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-8 px-5 py-16 md:grid-cols-12 md:items-center md:px-10 md:py-24">
          <div className="md:col-span-7">
            <div className="font-mono-label text-[10px] tracking-[0.26em]" style={{ color: ORANGE }}>Next step</div>
            <h2 className="font-display mt-3 font-black leading-[1.02] tracking-tight" style={{ fontSize: "clamp(28px, 3.6vw, 44px)" }} data-testid="cta-heading">
              In Our Area? Let's Get You a Price.
            </h2>
            <p className="mt-4 max-w-[560px] text-[15px] leading-[1.65] text-white/80 md:text-base">
              Tell us what you need and we'll send a real number back, usually the same day.
            </p>
          </div>
          <div className="flex flex-col gap-3 md:col-span-5 md:items-end">
            <a href={quoteHref} data-testid="cta-quote" className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-sm px-7 text-[15px] font-black text-white md:w-auto" style={{ backgroundColor: ORANGE }}>
              Get My Free Quote <ArrowRight size={16} weight="bold" />
            </a>
            <a href={PHONE_HREF} data-testid="cta-call" className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-sm border-2 border-white/70 px-7 text-[15px] font-black text-white md:w-auto">
              <Phone size={16} weight="fill" /> Call or Text {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <SiteFooter quoteHref="/#quote" />
      <MobileActionBar primaryLabel="Get My Free Quote" primaryHref={quoteHref} phoneHref={PHONE_HREF} textBody="Hi HCOB Property Services — do you serve my area? My ZIP is " watch={["#top", "#quote-cta"]} accent={ORANGE} testid="service-area-mobile-bar" />
    </div>
  );
}
